import React, { useState } from "react";
import { ArrowLeftRight, CalendarPlus, Loader2, MoreHorizontal, Send, ArchiveRestore } from "lucide-react";
import { toast } from "sonner";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { SCHOOL_COLOR, PORTFOLIO_COLOR, FLOW_LABEL, flowState, compareState, openPortfolioAssessment } from "./portfolioFlow";

const PILL = {
  not_started: "bg-slate-100 text-slate-700 border-slate-300",
  in_progress: "bg-amber-50 text-amber-800 border-amber-300",
  final: "bg-emerald-50 text-emerald-800 border-emerald-300",
  muted: "bg-slate-50 text-slate-600 border-slate-200",
};
const Pill = ({ state, label, testid }) => (
  <span data-testid={testid} className={`inline-flex h-7 w-[112px] items-center justify-center rounded-full border text-xs font-medium ${PILL[state] || PILL.muted}`}>
    {label ?? `${state === "final" ? "✓ " : ""}${FLOW_LABEL[state]}`}
  </span>
);
const BTN = "inline-flex h-11 sm:h-9 w-full items-center justify-center gap-1.5 rounded-md px-2 text-[13px] font-semibold text-white disabled:opacity-60";

export const SchoolsGridHeader = () => (
  <div className="msx-sgrid msx-sgrid-head px-4 pb-1 text-[10.5px] font-semibold uppercase tracking-normal text-slate-600" data-testid="schools-grid-header">
    <span className="c-name">School</span><span className="c-s whitespace-nowrap">School diagnostic</span><span className="c-p whitespace-nowrap">Your assessment</span>
    <span className="c-next">Next step</span><span className="c-more" aria-hidden />
  </div>
);

// One row of the Schools grid: shared column tracks, exactly one next-step action, ⋯ overflow menu.
export const SchoolGridRow = ({ row, entry, hasAnyPeriod, pid, canManage, canArchive, busy, navigate, onEdit, onArchive, onResend, onUnarchive, onRemindPeriod }) => {
  const [opening, setOpening] = useState(false);
  const remindedRecently = !!row.period_name_reminded_at && Date.now() - new Date(row.period_name_reminded_at).getTime() < 864e5;
  const archived = row.status === "archived";
  const active = row.kind === "active" && !archived;
  const reqState = entry ? flowState(entry.req.status) : null;
  const diagState = flowState(row.diagnostic_status);
  const s = !entry ? diagState : (reqState === "not_started" && diagState === "in_progress" ? "in_progress" : reqState);
  const p = entry ? flowState(entry.req.portfolio_status) : "not_started";
  const included = !!entry?.period.enable_portfolio_assessments;
  const cs = entry ? compareState({ school: s, portfolio: p, included }) : { enabled: false };
  const openMine = async () => {
    setOpening(true);
    try { await openPortfolioAssessment(pid, entry.period.id, row.tenant_id); } catch (e) { toast.error(e.message); setOpening(false); }
  };
  const compare = () => navigate(`/portfolio/compare/${entry.period.id}/${row.tenant_id}?pid=${pid}`);

  let next = null;
  if (archived) {
    next = canArchive && <button onClick={() => onUnarchive(row)} disabled={busy} data-testid={`school-unarchive-${row.id}`} className={`${BTN} bg-slate-600`}><ArchiveRestore className="h-4 w-4" /> Restore</button>;
  } else if (row.kind === "pending") {
    next = canManage && <button onClick={() => onResend(row)} disabled={busy} data-testid={`school-resend-${row.id}`} className={`${BTN} bg-slate-700`}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Resend invite</button>;
  } else if (!entry && active && !hasAnyPeriod) {
    next = canManage
      ? <button onClick={() => navigate(`/portfolio/periods?new=1&school=${row.tenant_id}&pid=${pid}&from=schools`)} data-testid={`school-create-period-${row.id}`}
          className={`${BTN} bg-[#007a9e]`}><CalendarPlus className="h-4 w-4 shrink-0" /> Create period</button>
      : <span className="block text-center text-xs text-slate-600" data-testid={`school-compare-reason-${row.id}`}>No diagnostic period yet</span>;
  } else if (!entry && active) {
    next = <span className="block text-center text-xs text-slate-600" data-testid={`school-compare-reason-${row.id}`}>Not in this period</span>;
  } else if (entry && included && p !== "final") {
    next = <button onClick={openMine} disabled={opening} data-testid={`school-assess-${row.id}`} className={BTN} style={{ background: PORTFOLIO_COLOR }}>
      {opening && <Loader2 className="h-4 w-4 animate-spin" />}{p === "in_progress" ? "Continue yours" : "Start yours"}</button>;
  } else if (entry && s === "not_started") {
    next = <span className="block text-center text-xs text-slate-600" data-testid={`school-compare-reason-${row.id}`}>Waiting for the school</span>;
  } else if (entry && cs.enabled) {
    next = <button onClick={compare} data-testid={`school-compare-${row.id}`} className={BTN} style={{ background: SCHOOL_COLOR }}>
      <ArrowLeftRight className="h-4 w-4 shrink-0" /> Compare
      <span className={`w-[78px] shrink-0 rounded-full text-center text-[10.5px] ${s === "final" && p === "final" ? "bg-white/20 ring-1 ring-white/40" : "bg-white text-amber-800"}`}>{s === "final" && p === "final" ? "Ready" : "Provisional"}</span></button>;
  } else {
    next = <span className="block text-center text-xs text-slate-600" data-testid={`school-compare-reason-${row.id}`}>{cs.reason || "No diagnostic period yet"}</span>;
  }

  return (
    <div data-testid={`school-row-${row.id}`} className={`msx-sgrid rounded-xl border p-4 ${archived ? "border-slate-200 bg-slate-50" : "border-slate-200 bg-white"}`}>
      <div className="c-name min-w-0">
        {active ? (
          <button onClick={() => navigate(`/portfolio/schools/${row.tenant_id}?pid=${pid}`)} data-testid={`school-name-link-${row.id}`}
            className="text-left font-semibold text-[#00607d] underline break-words">{row.school_name}</button>
        ) : <div className="font-semibold text-slate-900 break-words">{row.school_name}</div>}
        <div className="text-[13px] text-slate-600 break-words">{row.kind === "pending" ? (row.contact?.email || "Invited") : (entry?.period.name || row.period_label || "Diagnostic workspace")}</div>
      </div>
      <div className="c-s">
        {active ? <Pill state={s} testid={`school-chip-school-${row.id}`} /> : <Pill state="muted" label={archived ? "Archived" : "Invited"} testid={`school-chip-school-${row.id}`} />}
        {active && s === "in_progress" && row.answered != null && row.total_indicators ? (
          <div className="mt-1 w-[112px] text-center text-[11.5px] text-slate-600" data-testid={`school-progress-${row.id}`}>{row.answered} of {row.total_indicators} answered</div>
        ) : null}
      </div>
      <div className="c-p">
        {active && included ? <Pill state={p} testid={`school-chip-portfolio-${row.id}`} /> : <Pill state="muted" label={active ? "Not included" : "—"} testid={`school-chip-portfolio-${row.id}`} />}
      </div>
      <div className="c-next min-w-0">{next}</div>
      <div className="c-more flex justify-end">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button aria-label={`More actions for ${row.school_name}`} data-testid={`school-more-${row.id}`}
              className="grid h-11 w-11 sm:h-9 sm:w-9 place-items-center rounded-md text-slate-600 hover:bg-slate-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#007a9e]">
              <MoreHorizontal className="h-5 w-5" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" collisionPadding={8} className="w-52">
            {active && <DropdownMenuItem disabled={s === "not_started"} onSelect={() => navigate(`/portfolio/schools/${row.tenant_id}/diagnostic?pid=${pid}`)} data-testid={`school-enter-${row.id}`}>School's diagnostic</DropdownMenuItem>}
            {active && included && <DropdownMenuItem onSelect={openMine} data-testid={`school-menu-assess-${row.id}`}>Your assessment</DropdownMenuItem>}
            {active && <DropdownMenuItem onSelect={() => navigate(`/portfolio/schools/${row.tenant_id}`)} data-testid={`school-open-${row.id}`}>Details</DropdownMenuItem>}
            {canManage && !archived && <DropdownMenuItem onSelect={() => onEdit(row)} data-testid={`school-edit-${row.id}`}>Edit</DropdownMenuItem>}
            {canManage && active && row.needs_period_name && (
              <DropdownMenuItem disabled={remindedRecently} onSelect={() => onRemindPeriod(row)} data-testid={`school-remind-period-${row.id}`}>
                {remindedRecently ? "Reminder sent today" : "Remind to name period"}
              </DropdownMenuItem>
            )}
            {canArchive && !archived && <DropdownMenuItem onSelect={() => onArchive(row)} data-testid={`school-archive-${row.id}`} className="text-red-700">Archive</DropdownMenuItem>}
            {archived && canArchive && <DropdownMenuItem onSelect={() => onUnarchive(row)}>Restore</DropdownMenuItem>}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
};
